// The org-admin cockpit's sidebar definition, kept out of OrgAdminLayout so the
// employee shell can render the same entries. An employee sees only the ones an
// admin has granted them, so the two menus must be built from one list — a
// second copy here would drift the moment a section is added.
import {
  DashboardIcon,
  AiIcon,
  ClientsIcon,
  CredentialsIcon,
  ServicesIcon,
  UsersIcon,
  PermissionsIcon,
  NoticeIcon,
  ClientNotesIcon,
  DscIcon,
  PhysicalFilesIcon,
  SettingsIcon,
  TasksIcon,
  TrashIcon,
  ToDoIcon,
  RetainersIcon,
  FinanceIcon,
  AttendanceIcon,
  RenewalsIcon,
  DocumentsIcon,
  ReportsIcon,
  AuditLogsIcon,
  SendNotificationsIcon,
  WebsiteIcon,
  PlusIcon,
  ExternalLinkIcon,
  InboxIcon,
  ChatIcon,
  CheckCircleIcon,
  XCircleIcon,
  CalendarIcon,
} from './NavIcons';
import OrgAdminLogo from './OrgAdminLogo';
import { visibleNavItems, isSection, flattenNavItems } from '../../lib/navAccess';

// ─── The shape of this list ─────────────────────────────────────────────────
//
// ⚠️ IT WAS TWENTY-SEVEN ENTRIES IN ONE FLAT COLUMN, and that is the bug this
// fixes. A list that long is not read, it is scanned — so the four things
// people open constantly were no easier to find than the one they open twice a
// year, and every feature added made the rest slightly harder to reach.
//
// The list is now three PINNED entries followed by seven `{ section, items }`
// groups. A section carries no `permission` of its own: it is shown when
// anything inside it survives the permission filter and dropped when nothing
// does (see `lib/navAccess.js`). Giving a group its own key would be a second
// switch for a decision its children already make.
//
// `permission` is a `nav.*` key from the auth-service permission catalog
// (auth-service/src/data/permissionCatalog.js) — keep the two in step. It
// controls only whether the entry is rendered; what the user may *do* on the
// page behind it is still governed by that section's action permissions.
// Entries with no `permission` (the external website link) are never gated.
export const ORG_NAV_ITEMS = [
  // ─── PINNED — always visible, never collapses ────────────────────────────
  //
  // ⚠️ THESE THREE NEVER COLLAPSE, and that is what the grouping below pays
  // for. Everything else is one click away; these are in the same place on
  // every page, every time.
  //
  // The fourth pinned thing — searching clients — is the TopBar's search box,
  // not a menu entry (see `buildNavChrome`'s `searchPlaceholder`). It is
  // already always on screen and above the sidebar; giving it a row here as
  // well would be two controls for one job.
  { to: '/org-admin', icon: <DashboardIcon />, label: 'Dashboard', permission: 'nav.dashboard' },
  // ⚠️ PINNED, not filed under Tasks. It was a Tasks child while it only
  // created tasks; it now searches clients, notices, documents, invoices, DSCs,
  // files, renewals, services and people as well, so filing it under one of the
  // eleven things it searches was actively misleading.
  //
  // ⚠️ GATED ON `nav.dashboard`, WHICH IS THE WEAKEST KEY IN THE COCKPIT — and
  // that is correct HERE, unlike anywhere else. The page itself grants nothing:
  // utils/command/search.js queries a source only if the caller holds that
  // section's own key, and /api/ai/execute re-checks tasks.create before any
  // write. So somebody with only nav.dashboard opens it and finds nothing but
  // an empty box — which is the honest outcome. Gating the TAB on a section key
  // instead would hide the search from exactly the people who most need a way
  // to find things without knowing which menu they live under.
  { to: '/org-admin/ai', icon: <AiIcon />, label: 'AI', permission: 'nav.dashboard' },
  // Gated on nav.clients, like the API: a Tally company IS a client, and the
  // key that governs seeing clients governs seeing which of them are in Tally.
  //
  // ⚠️ `status: 'tally'` ASKS THE SHELL FOR A SYNC DOT — a connector that has
  // quietly stopped syncing is the failure that module exists to prevent, and
  // the menu is the one surface seen on every page. The value is fetched behind
  // a cache (`lib/tallyNavStatus.js`), never by this module: this is a pure
  // list and must stay renderable without a network.
  { to: '/org-admin/tally', icon: <ClientsIcon />, label: 'Tally', permission: 'nav.clients', status: 'tally' },

  // ─── WORK — the daily queue ──────────────────────────────────────────────
  //
  // ⚠️ ORDERED BY WHEN YOU TOUCH IT, not alphabetically: what you owe today
  // (To-Do), what is waiting on you (Approvals), and what lapses if nobody
  // looks (Renewals). What you owe today and what is coming are PINNED above.
  {
    section: 'Work',
    items: [
      { to: '/org-admin/todos', icon: <ToDoIcon />, label: 'To-Do', permission: 'nav.todos', countKey: 'todo' },
      // ⚠️ TASKS AND COMPLIANCE CALENDAR ARE NOT REPEATED HERE. They are in the
      // PINNED block above, which is on screen whether this group is open or
      // shut — so a copy in here is the same link twice on one panel, and the
      // second one is the one that is sometimes hidden. The cost this normally
      // carries (unpin it and you can no longer find it) does not apply while
      // the pinned block is fixed rather than user-configurable.
      //
      // ⚠️ APPROVALS KEEPS ITS OWN CHEVRON, so it sits three deep once this
      // section is closed around it. That is the deepest anything here may go —
      // a fourth level is unreadable in a 240px rail, and the answer at that
      // point is to move the sub-views onto the page as tabs rather than nest.
      {
        icon: <InboxIcon />,
        label: 'Approvals',
        permission: 'nav.approvals',
        countKey: 'approvals',
        notify: 'approvals',
        children: [
          { to: '/org-admin/approvals', icon: <InboxIcon />, label: 'Pending' },
          { to: '/org-admin/approvals/approved', icon: <CheckCircleIcon />, label: 'Approved' },
          { to: '/org-admin/approvals/rejected', icon: <XCircleIcon />, label: 'Rejected' },
        ],
      },
      // Gated on nav.clients — a renewal is a client's licence, and the server
      // gates /api/expirables the same way.
      { to: '/org-admin/renewals', icon: <RenewalsIcon />, label: 'Renewals', permission: 'nav.clients' },
    ],
  },

  // ─── CLIENTS — the relationship, and what it is worth ────────────────────
  {
    section: 'Clients',
    items: [
      // ⚠️ "All Clients", not "Clients", now that it sits under a heading that
      // already says Clients. A group whose first child repeats its own name
      // reads as though that child IS the group and the rest are exceptions.
      { to: '/org-admin/clients', icon: <ClientsIcon />, label: 'All Clients', trailing: <PlusIcon />, permission: 'nav.clients' },
      // The firm's half of the client-portal conversation. Gated on nav.clients
      // — it's client data, and the server gates /api/client-requests the same.
      { to: '/org-admin/client-requests', icon: <ClientsIcon />, label: 'Client Requests', permission: 'nav.clients', notify: 'client-requests' },
      // Live chat with clients. Same gate and same reasoning as Client Requests
      // — it is the same conversation, just synchronous.
      { to: '/org-admin/chat', icon: <ChatIcon />, label: 'Client Chat', permission: 'nav.clients', notify: 'chat' },
      // ⚠️ FINANCE AND RETAINERS SIT TOGETHER AND BOTH GATE ON `nav.finance` —
      // they are two halves of one collection (see the Retainers note in
      // CLAUDE.md), so separating them would put one question in two places.
      // They are under Clients rather than in a money group of their own
      // because `nav.finance` is already what hides them from anyone who should
      // not see revenue; a heading of its own would advertise the section to
      // exactly the people it is being hidden from.
      { to: '/org-admin/finance', icon: <FinanceIcon />, label: 'Finance', permission: 'nav.finance' },
      { to: '/org-admin/retainers', icon: <RetainersIcon />, label: 'Retainers', permission: 'nav.finance' },
    ],
  },

  // ─── RECORDS — everything stored about a client ──────────────────────────
  //
  // ⚠️ CREDENTIALS AND DSC ARE SPLIT OUT INTO "Vault" BELOW, and the reason is
  // SIZE rather than permissions. Checked the permission question first and it
  // argues the other way: every entry in this group already carries its own key
  // (`nav.documents`, `nav.physical-files`, `nav.notices`,
  // `client-notes.access`, `nav.credentials`, `nav.dsc`), so a Vault heading
  // mirrors no access boundary the other four don't also have.
  //
  // What forced it is the rule this file sets itself: past five children a
  // group's own strip starts scrolling and hiding its contents, which is the
  // twenty-seven-entry bug one level down. Records held six. Credentials and
  // DSC are the honest seam to cut on — everything left is a RECORD of
  // something, those two are SECRETS, with a `CredentialAccessLog` row per
  // reveal and an Admin-only bulk import to prove the difference is real.
  {
    section: 'Records',
    items: [
      { to: '/org-admin/documents', icon: <DocumentsIcon />, label: 'Documents', permission: 'nav.documents' },
      { to: '/org-admin/physical-files', icon: <PhysicalFilesIcon />, label: 'Physical Files', permission: 'nav.physical-files' },
      { to: '/org-admin/notices', icon: <NoticeIcon />, label: 'Notices', permission: 'nav.notices', notify: 'notices' },
      // ⚠️ "Notes", NOT "Client Notes". A note no longer has to be about a
      // client: the label named a restriction the feature does not have, and a
      // general note filed under a client-shaped entry is one nobody looks for.
      //
      // ⚠️ GATED ON `client-notes.access`, NOT on a `nav.*` key — the one
      // deviation from the rule stated at the top of this file, and deliberate.
      // Every other entry needs its own nav key because the page behind it is
      // gated by role or by a different action permission, so the two have to
      // be said separately. Here the API router already IS
      // `requirePermission('client-notes.access')`; minting a `nav.notes`
      // beside it would be a second switch for one decision, and both drift
      // directions are bad — an entry visible to someone every request 403s
      // for, or hidden from someone entitled to read. It also needs no catalog
      // entry and no `migrate:permissions` run to start working. (The KEY keeps
      // its old name on purpose; renaming it would revoke the grant from every
      // role already holding it. See the auth-service catalog.)
      { to: '/org-admin/notes', icon: <ClientNotesIcon />, label: 'Notes', permission: 'client-notes.access' },
    ],
  },

  // ─── VAULT — the secrets, kept apart from the records ────────────────────
  //
  // ⚠️ A GROUP OF TWO, AND IT EARNS ITS HEADING. It is what brought Records
  // back under the five-child ceiling, and the line it draws is one people
  // already hold in their heads: a document is a record of something that
  // happened, a credential is a live key to somebody else's portal. See
  // CREDENTIAL_VAULT.md for why that distinction is more than presentational.
  {
    section: 'Vault',
    items: [
      { to: '/org-admin/credentials', icon: <CredentialsIcon />, label: 'Credentials', permission: 'nav.credentials' },
      // `notify: 'dsc'` lights the sidebar dot from the daily expiry digest —
      // the one notification in this app that nobody triggered, because an
      // expiry announces itself only by a date arriving.
      { to: '/org-admin/dsc', icon: <DscIcon />, label: 'DSC', permission: 'nav.dsc', notify: 'dsc' },
    ],
  },

  // ─── TEAM — the people, and what they did ────────────────────────────────
  {
    section: 'Team',
    items: [
      { to: '/org-admin/users', icon: <UsersIcon />, label: 'Users', permission: 'nav.users' },
      { to: '/org-admin/permissions', icon: <PermissionsIcon />, label: 'Permissions', permission: 'nav.permissions' },
      // Gated on nav.users: seeing the whole team's hours is a people-management
      // view, and that is the key which already means "you manage people here".
      { to: '/org-admin/attendance', icon: <AttendanceIcon />, label: 'Attendance', permission: 'nav.users' },
      // The firm's own trail. Its own key rather than riding on nav.reports: a
      // report is an aggregate of the work, this is a record of who did it, and
      // a firm may well want a compliance partner reading the trail without the
      // revenue reports beside it.
      { to: '/org-admin/audit-logs', icon: <AuditLogsIcon />, label: 'Audit Logs', permission: 'nav.audit-logs' },
    ],
  },

  // ─── INSIGHTS ────────────────────────────────────────────────────────────
  //
  // ⚠️ ONE ENTRY, AND THAT IS FINE. A group of one reads as a category label,
  // which is exactly what this is — and it gives the next aggregate view an
  // obvious home instead of leaving Reports orphaned at the bottom of a list.
  // The alternative was pinning it, which would claim it is opened as often as
  // the dashboard. It is not.
  {
    section: 'Insights',
    items: [
      { to: '/org-admin/reports', icon: <ReportsIcon />, label: 'Reports', permission: 'nav.reports' },
    ],
  },

  // ─── SETUP — configured once, then left alone ────────────────────────────
  //
  // ⚠️ SERVICES IS HERE BECAUSE IT IS MASTER DATA. If a firm turns out to edit
  // its service catalog weekly, that is the signal to move it up beside All
  // Clients — this grouping is a claim about how often a thing is touched, and
  // a wrong claim costs a click every single time.
  {
    section: 'Setup',
    items: [
      { to: '/org-admin/services', icon: <ServicesIcon />, label: 'Services', permission: 'nav.services' },
      // Gated on nav.users, the same reasoning Attendance carries: announcing
      // something to the firm's staff is a people-management act. The API
      // re-checks Manager+ on the send itself.
      { to: '/org-admin/notifications', icon: <SendNotificationsIcon />, label: 'Send Notifications', permission: 'nav.users' },
      { to: '/org-admin/settings', icon: <SettingsIcon />, label: 'Settings', permission: 'nav.settings' },
      { external: true, href: '#', icon: <WebsiteIcon />, label: 'Your Website', trailing: <ExternalLinkIcon /> },
    ],
  },
];



// ─── PINNED — the two pages people open every morning ───────────────────────
//
// ⚠️ IT IS A SHORTCUT, NOT A SECTION. Both of these still live in Work, and
// removing them from there would mean somebody who unpinned Tasks could no
// longer find Tasks. The block exists so the daily pages are reachable without
// opening a group first, which is exactly what grouping cost.
//
// ⚠️ TASKS CARRIES ITS OWN CHEVRON HERE, and it has to. These two no longer
// appear in Work as well (a link twice on one panel, the second copy the one
// that hides), so this row is the ONLY menu path to "Deleted Tasks" — pinning
// the bare list link would leave that page reachable by URL and nothing else.
// It expands in place like any other parent; the block itself never collapses.
//
// ⚠️ NOT YET USER-CONFIGURABLE. The design calls for a Pin action on any item;
// what is built is the block, the defaults and the persistence. The affordance
// to change them is not here yet, so this list is currently what everyone gets.
export const PINNED_DEFAULTS = ['/org-admin/tasks', '/org-admin/compliance-calendar'];

export const ORG_PINNED_ITEMS = [
  {
    icon: <TasksIcon />,
    label: 'Tasks',
    permission: 'nav.tasks',
    // ⚠️ `countKey` AND `notify` ARE DIFFERENT THINGS and both belong here.
    // `notify` is how many notifications you have not READ; `countKey` is how
    // much work is OPEN. A firm with 29 live tasks and nothing unread should
    // see 29, not nothing — the design's pills are workload, not an inbox.
    countKey: 'tasks',
    notify: 'tasks',
    children: [
      { to: '/org-admin/tasks', icon: <TasksIcon />, label: 'Tasks' },
      { to: '/org-admin/tasks/deleted', icon: <TrashIcon />, label: 'Deleted Tasks' },
    ],
  },
  {
    to: '/org-admin/compliance-calendar',
    icon: <CalendarIcon />,
    label: 'Compliance Calendar',
    permission: 'nav.compliance-calendar',
    countKey: 'compliance',
    countTone: 'alert',
  },
];

// The employee area's own menu. Lives here beside ORG_NAV_ITEMS because the two
// are assembled together below — an employee's sidebar is this list plus
// whichever cockpit sections an admin has granted them.
//
// ⚠️ DELIBERATELY LEFT FLAT. It is eight entries; grouping eight things is
// ceremony that costs a click and explains nothing. The shell renders a flat
// list and a sectioned one from the same code path, so this needs no change.
export const EMPLOYEE_NAV_ITEMS = [
  { to: '/employee', icon: <DashboardIcon />, label: 'Home' },
  { to: '/employee/tasks', icon: <TasksIcon />, label: 'My Tasks', notify: 'tasks' },
  { to: '/tasks/create', icon: <PlusIcon />, label: 'Create Task' },
  { to: '/employee/todos', icon: <ToDoIcon />, label: 'My To-Dos' },
  // Team chat, not client chat — the cockpit's Chat page (behind nav.clients)
  // carries both, this route carries only the staff-to-staff half.
  { to: '/employee/chat', icon: <ChatIcon />, label: 'Team Chat' },
  { to: '/employee/attendance', icon: <AttendanceIcon />, label: 'Attendance' },
  { to: '/compliance', icon: <CalendarIcon />, label: 'Compliance' },
  { divider: true },
  { to: '/employee/profile', icon: <UsersIcon />, label: 'Profile' },
];

// Partner (4) and above land in the firm cockpit; Manager and below land in the
// employee area. Mirrors the routing decision Login.jsx makes after sign-in —
// keep the two in step.
const COCKPIT_MIN_LEVEL = 4;

export const isCockpitUser = (user) =>
  Boolean(user?.isSuperAdmin) || (user?.role?.level ?? 0) >= COCKPIT_MIN_LEVEL;

// The shell a given user gets, whichever page they are standing on.
//
// This is deliberately keyed to the *user*, not the route. Granting an employee
// a cockpit section puts a link to an /org-admin/* page in their sidebar, and
// those pages render OrgAdminLayout — so without this an employee who clicked
// "Clients" was thrown into the Org Admin shell: different brand, different
// search, and the cockpit's ungated entries on show. They stay in their own
// shell now; only the page content changes.
export function buildNavChrome(user) {
  if (isCockpitUser(user)) {
    return {
      brandText: 'Org Admin',
      brandIcon: undefined,
      items: visibleNavItems(ORG_NAV_ITEMS, user),
      // Filtered by the same rule as the menu proper — a pinned shortcut to a
      // section somebody cannot open would be a 403 waiting on the one row
      // they are most likely to click.
      pinned: visibleNavItems(ORG_PINNED_ITEMS, user),
      searchPlaceholder: 'Search Clients',
      searchPath: (q) => `/org-admin/clients?q=${encodeURIComponent(q)}`,
    };
  }

  // Strict: an employee sees a cockpit section only where it was granted. The
  // cockpit's fail-open rule would hand them all of it.
  //
  // ⚠️ FLATTENED BACK OUT OF ITS GROUPS. An employee granted two cockpit
  // sections would otherwise get two headings to expand before reaching two
  // links — the grouping earns its keep at twenty-seven entries and is pure
  // overhead at two. Their own eight entries stay flat for the same reason.
  //
  // ⚠️ THE PINNED BLOCK IS PART OF THE SOURCE LIST HERE. Tasks and Compliance
  // Calendar live only in ORG_PINNED_ITEMS now, and an employee has no pinned
  // block of their own — reading ORG_NAV_ITEMS alone would silently revoke the
  // cockpit Tasks and Calendar links from every employee granted those keys.
  const granted = visibleNavItems([...ORG_NAV_ITEMS, ...ORG_PINNED_ITEMS], user, { strict: true })
    .flatMap((entry) => (isSection(entry) ? entry.items : [entry]))
    .filter((i) => i.permission);

  return {
    brandText: 'Employee',
    brandIcon: <OrgAdminLogo />,
    // No pinned block: an employee's menu is eight entries and already flat,
    // so a shortcut to one of them saves nothing.
    pinned: [],
    items: granted.length ? [...EMPLOYEE_NAV_ITEMS, { divider: true }, ...granted] : EMPLOYEE_NAV_ITEMS,
    searchPlaceholder: 'Search My Tasks',
    searchPath: (q) => `/employee/tasks?q=${encodeURIComponent(q)}`,
  };
}

/**
 * Hang unread counts off the nav entries that declared a `notify` section.
 *
 * Applied to the OUTPUT of buildNavChrome rather than inside it, because the
 * chrome is memoised on `user` (it rebuilds icons and runs the permission
 * filter) and the counts change on every notification — folding them together
 * would rebuild the entire menu each time a badge ticks.
 *
 * ⚠️ A PARENT SHOWS THE SUM OF ITS OWN SECTION, NOT ITS CHILDREN'S. The
 * children here are views of one section ("Tasks" / "Deleted Tasks"), not
 * separate ones, so summing them would double-count. When a genuinely
 * multi-section parent appears, it declares no `notify` of its own and this is
 * where the sum would go.
 *
 * ⚠️ A SECTION ALSO CARRIES THE SUM OF ITS ITEMS, and it has to: a group
 * collapses, so an unread badge on an entry inside a closed one is invisible —
 * which is the whole point of a badge. `sectionBadge` is kept separate from
 * `badge` so the shell can render it quietly beside the heading rather than as
 * a second copy of a count that is already shown one row below when open.
 */
export function applyNavBadges(items, countFor) {
  if (typeof countFor !== 'function') return items;

  const withBadge = (item) => {
    if (!item.notify) return item;
    const n = countFor(item.notify);
    // `badge` is what AppShell's sidebar renders; leaving it undefined is what
    // makes "no unread" mean "no dot" rather than a grey zero.
    return n > 0 ? { ...item, badge: n > 99 ? '99+' : String(n) } : item;
  };

  return items.map((entry) => {
    if (!isSection(entry)) return withBadge(entry);
    const inner = entry.items.map(withBadge);
    const total = entry.items.reduce((sum, i) => sum + (i.notify ? countFor(i.notify) || 0 : 0), 0);
    return {
      ...entry,
      items: inner,
      sectionBadge: total > 0 ? (total > 99 ? '99+' : String(total)) : undefined,
    };
  });
}

// route path -> the nav key that unlocks it, longest match first so
// "/org-admin/tasks/deleted" resolves to Tasks rather than to Dashboard
// ("/org-admin" is a prefix of every cockpit route).
//
// ⚠️ THIS WALKS SECTIONS TOO. It feeds `requiredNavKeyFor`, which is the ROUTE
// GUARD — a path this misses is a page with no gate on it at all, so a nav
// entry that moved inside a group must not fall out of the map. `permission`
// is read from the ITEM, never from the section: a group has no key of its own.
// ⚠️ IT WALKS THE PINNED BLOCK TOO. Tasks and Compliance Calendar live ONLY in
// ORG_PINNED_ITEMS now; reading ORG_NAV_ITEMS alone would drop /org-admin/tasks,
// /org-admin/tasks/deleted and /org-admin/compliance-calendar out of this map,
// and `requiredNavKeyFor` answering null means no gate on those pages AT ALL.
const ROUTE_KEYS = [...ORG_NAV_ITEMS, ...ORG_PINNED_ITEMS].flatMap((entry) =>
  (isSection(entry) ? entry.items : [entry]).flatMap((item) =>
    (item.children || [item])
      .filter((sub) => sub.to)
      .map((sub) => ({ to: sub.to, permission: item.permission }))
  )
).sort((a, b) => b.to.length - a.to.length);

export function requiredNavKeyFor(pathname) {
  const hit = ROUTE_KEYS.find((r) => pathname === r.to || pathname.startsWith(`${r.to}/`));
  return hit?.permission || null;
}

// Whether `user` may open a cockpit route. Mirrors the sidebar exactly, so a
// section that is hidden is also unreachable by typing its URL.
export function canAccessNavKey(user, key) {
  if (!key) return true; // a cockpit route with no nav entry of its own
  if (user?.isSuperAdmin) return true;

  const permissions = user?.permissions || [];
  if (permissions.includes(key)) return true;

  // Same fail-open the cockpit sidebar uses, and for the same reason: a
  // Partner-level custom role nobody has curated yet must not be locked out of
  // its own cockpit. Employees never qualify — they hold no cockpit grant by
  // default, which is exactly the state this must keep denying.
  const configured = permissions.some((p) => typeof p === 'string' && p.startsWith('nav.'));
  return !configured && isCockpitUser(user);
}

// Re-exported so callers that already import from this module (the shell, the
// route guard) need not know that the flattening rule lives in navAccess.
export { isSection, flattenNavItems };
